import {
  ValueFormatterParams,
  GridOptions,
  EditableCallbackParams,
  NewValueParams,
  IRowNode,
} from 'ag-grid-community'
import { CellClassParams, ValueGetterParams } from 'ag-grid-enterprise'
import { intl } from '../../../../../i18n'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  ColumnType,
  columnTypes,
  defaultColDef,
  frameworkComponents,
} from '../../../../containers/commons/AgGrid'
import { getRowNumber } from '../../../../containers/BulkSheetView/lib/gridApi'
import { GanttRowItem } from '../../../../containers/commons/AgGrid/components/cell/custom/gantt/GanttRowItem'
import {
  GanttBarColorName,
  getInheritedGanttColor,
} from '../../../../containers/commons/AgGrid/components/cell/custom/gantt/ganttUtil'
import WbsItemStatusCell from '../../../../containers/commons/AgGrid/components/cell/custom/wbsItemStatus'
import { ComputedMasterScheduleRowData, MasterScheduleRowData } from './model'
import { ProjectPlanNameCellRenderer } from '../grid/renderers/ProjectPlanNameCellRenderer'
import { colorPalette } from '../../../../style/colorPalette'
import { ReportStatusCellRenderer } from '../cell/ReportStatusCellRenderer'
import { ReportStatusCellEditor } from '../cell/ReportStatusCellEditor'
import { handleStatusCellKeyboardEvent } from '../cell/handleStatusCellKeyboardEvent'
import { VersionReportStatus } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'

type UseMasterScheduleGridOptionsProps = {
  editable: boolean
  onChangeStatus: (
    data: ComputedMasterScheduleRowData,
    status: VersionReportStatus,
    rowIndex: number
  ) => void
  wbsItemGanttColors: ReadonlyMap<string, GanttBarColorName>
}

// Ag grid options
export const useMasterScheduleGridOptions = ({
  editable,
  onChangeStatus,
  wbsItemGanttColors,
}: UseMasterScheduleGridOptionsProps): GridOptions => {
  const onChangeStatusRef = useRef(onChangeStatus)
  useEffect(() => {
    onChangeStatusRef.current = onChangeStatus
  }, [onChangeStatus])
  const wbsItemGanttColorsRef = useRef(wbsItemGanttColors)
  useEffect(() => {
    wbsItemGanttColorsRef.current = wbsItemGanttColors
  }, [wbsItemGanttColors])
  const getGanttRowItemWithColor = useCallback(
    (data: unknown, node?: IRowNode) =>
      getGanttRowItem(data, node, wbsItemGanttColorsRef.current),
    []
  )
  const gridOptions = useMemo(
    () => ({
      // Grid context
      context: {},
      // Styling
      groupHeaderHeight: 25,
      headerHeight: 45,
      rowHeight: 45,
      // Row grouping
      treeData: true,
      // TODO: delete this option if the expanded condition is saved.
      groupDefaultExpanded: -1,
      autoGroupColumnDef: {
        field: 'wbsItem.displayName',
        headerName: intl.formatMessage({
          id: 'projectOverview.displayName',
        }),
        width: 300,
        hide: false,
        pinned: true,
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: ProjectPlanNameCellRenderer,
        },
      },
      // Column definition
      columnTypes: columnTypes(),
      components: frameworkComponents,
      defaultColDef: { ...defaultColDef(), hide: true },
      defaultColGroupDef: { suppressHeaderContextMenu: true },
      tooltipShowDelay: 100,
      suppressContextMenu: true,
      columnDefs: [
        {
          headerName: intl.formatMessage({ id: 'projectOverview.information' }),
          children: [
            {
              field: 'rowNumber',
              headerName: '#',
              pinned: true,
              lockPosition: 'left',
              suppressMovable: true,
              width: 50,
              hide: false,
              valueGetter: (params: ValueGetterParams<MasterScheduleRowData>) =>
                getRowNumber(params.node),
              cellClass: 'ag-numeric-cell',
              cellStyle: { justifyContent: 'center' },
            },
            {
              field: 'wbsItem.status',
              headerName: intl.formatMessage({
                id: 'projectOverview.processStatus',
              }),
              type: [ColumnType.wbsItemStatus],
              cellRenderer: WbsItemStatusCell.simpleCellRenderer,
              pinned: true,
              lockPosition: 'left',
              width: 90,
              hide: false,
            },
            {
              field: 'status',
              headerName: intl.formatMessage({ id: 'projectOverview.status' }),
              cellRenderer: ReportStatusCellRenderer,
              editable: (
                params: EditableCallbackParams<ComputedMasterScheduleRowData>
              ) => editable && params.data?.status != null,
              cellEditor: ReportStatusCellEditor,
              cellEditorPopup: true,
              cellEditorPopupPosition: 'under',
              suppressKeyboardEvent: handleStatusCellKeyboardEvent,
              onCellValueChanged: (
                params: NewValueParams<
                  ComputedMasterScheduleRowData,
                  VersionReportStatus
                >
              ) => {
                const rowIndex = params.node?.rowIndex
                if (!params.data || !params.newValue || rowIndex == null) return
                onChangeStatusRef.current(
                  params.data,
                  params.newValue,
                  rowIndex
                )
              },
              pinned: true,
              lockPosition: 'left',
              width: 90,
              hide: false,
              cellStyle: { justifyContent: 'center' },
            },
          ],
        },
        {
          headerName: intl.formatMessage({ id: 'projectOverview.term' }),
          children: [
            {
              field: 'wbsItem.scheduledDate.startDate',
              headerName: intl.formatMessage({
                id: 'projectOverview.scheduledStartDate',
              }),
              type: [ColumnType.date, ColumnType.wbsItemScheduledDate],
              width: 90,
            },
            {
              field: 'wbsItem.scheduledDate.endDate',
              headerName: intl.formatMessage({
                id: 'projectOverview.scheduledEndDate',
              }),
              type: [ColumnType.date, ColumnType.wbsItemScheduledDate],
              width: 90,
            },
            {
              field: 'wbsItem.actualDate.startDate',
              headerName: intl.formatMessage({
                id: 'projectOverview.actualStartDate',
              }),
              type: [ColumnType.date, ColumnType.wbsItemActualDate],
              width: 90,
            },
            {
              field: 'wbsItem.actualDate.endDate',
              headerName: intl.formatMessage({
                id: 'projectOverview.actualEndDate',
              }),
              type: [ColumnType.date, ColumnType.wbsItemActualDate],
              width: 90,
            },
          ],
        },
        {
          headerName: intl.formatMessage({ id: 'projectOverview.progress' }),
          children: [
            {
              field: 'scheduledProgressRate',
              headerName: intl.formatMessage({
                id: 'projectOverview.scheduledProgressRate',
              }),
              hide: false,
              valueGetter: (
                params: ValueGetterParams<ComputedMasterScheduleRowData>
              ) => {
                if (!params.data) return NaN
                return params.data.progressValues.scheduledProgressRate
              },
              valueFormatter: percentageFormatter,
              cellStyle: progressValuesCellStyle,
            },
            {
              field: 'progressRate',
              headerName: intl.formatMessage({
                id: 'projectOverview.progressRate',
              }),
              hide: false,
              valueGetter: (
                params: ValueGetterParams<ComputedMasterScheduleRowData>
              ) => {
                if (!params.data) return NaN
                return params.data.progressValues.progressRate
              },
              valueFormatter: percentageFormatter,
              cellStyle: progressValuesCellStyle,
            },
          ],
        },
        {
          headerName: intl.formatMessage({ id: 'projectOverview.ganttChart' }),
          headerGroupComponent: 'ganttHeaderGroupComponent',
          children: [
            {
              field: 'ganttChart',
              headerName: intl.formatMessage({
                id: 'projectOverview.ganttChart',
              }),
              type: [ColumnType.gantt],
              cellRendererParams: {
                editable: false,
                readOnly: true,
                getGanttRowItem: getGanttRowItemWithColor,
              },
              hide: false,
              cellStyle: {
                borderColor: 'transparent',
                backgroundColor: 'transparent',
              },
              valueGetter: (
                params: ValueGetterParams<ComputedMasterScheduleRowData>
              ) => {
                const { data } = params
                if (!data?.wbsItem) return undefined
                const { wbsItem, progressValues } = data
                return JSON.stringify([
                  wbsItem.scheduledDate,
                  wbsItem.status,
                  wbsItem.actualDate,
                  progressValues,
                ])
              },
            },
          ],
        },
      ],
      processUnpinnedColumns: () => [],
    }),
    [editable, getGanttRowItemWithColor]
  )
  return gridOptions
}

const isComputedMasterScheduleRowData = (
  data: unknown
): data is ComputedMasterScheduleRowData => {
  return (
    !!data &&
    typeof data === 'object' &&
    'wbsItem' in data &&
    'progressValues' in data
  )
}

export const getGanttRowItem = (
  data: unknown,
  node?: IRowNode,
  wbsItemGanttColors?: ReadonlyMap<string, GanttBarColorName>
): GanttRowItem | undefined => {
  if (!isComputedMasterScheduleRowData(data)) return undefined
  const { wbsItem, progressValues } = data
  return {
    uuid: wbsItem.uuid,
    scheduledDate: wbsItem.scheduledDate,
    status: wbsItem.status,
    displayName: wbsItem.displayName,
    type: wbsItem.wbsItemType?.code ?? '',
    wbsItemType: wbsItem.wbsItemType,
    iconUrl: wbsItem.wbsItemType?.iconUrl,
    progressRate: Number.isNaN(progressValues.progressRate)
      ? undefined
      : progressValues.progressRate,
    scheduledProgressRate: Number.isNaN(progressValues.scheduledProgressRate)
      ? undefined
      : progressValues.scheduledProgressRate,
    color: getInheritedGanttColor(node, wbsItemGanttColors, 'wbsItem.uuid'),
  }
}

const progressValuesCellStyle = (
  params: CellClassParams<MasterScheduleRowData>
) => {
  return {
    fontSize: '20px',
    fontWeight: 600,
    color: colorPalette.monotone[9],
    justifyContent: 'flex-end',
  }
}
const percentageFormatter = (
  params: ValueFormatterParams<MasterScheduleRowData, number>
) => {
  const { value } = params
  if (value == null || Number.isNaN(value)) return '-'
  return `${(value * 100).toFixed(1)}%`
}
